/** Small numerical kernel shared by every Lakaran package. World units are millimetres, Y is up. */

export const TAU = Math.PI * 2;
export const EPS = 1e-9;

export class Vec2 {
  constructor(
    readonly x: number = 0,
    readonly y: number = 0,
  ) {}

  static of(x: number, y: number): Vec2 {
    return new Vec2(x, y);
  }

  static polar(radius: number, angle: number): Vec2 {
    return new Vec2(Math.cos(angle) * radius, Math.sin(angle) * radius);
  }

  static mid(a: Vec2, b: Vec2): Vec2 {
    return new Vec2((a.x + b.x) / 2, (a.y + b.y) / 2);
  }

  clone(): Vec2 {
    return new Vec2(this.x, this.y);
  }

  add(v: Vec2): Vec2 {
    return new Vec2(this.x + v.x, this.y + v.y);
  }

  sub(v: Vec2): Vec2 {
    return new Vec2(this.x - v.x, this.y - v.y);
  }

  scale(s: number): Vec2 {
    return new Vec2(this.x * s, this.y * s);
  }

  div(s: number): Vec2 {
    return new Vec2(this.x / s, this.y / s);
  }

  neg(): Vec2 {
    return new Vec2(-this.x, -this.y);
  }

  dot(v: Vec2): number {
    return this.x * v.x + this.y * v.y;
  }

  /** 2D cross product (z component of the 3D cross). */
  cross(v: Vec2): number {
    return this.x * v.y - this.y * v.x;
  }

  lenSq(): number {
    return this.x * this.x + this.y * this.y;
  }

  len(): number {
    return Math.hypot(this.x, this.y);
  }

  normalize(): Vec2 {
    const l = this.len();
    return l > EPS ? new Vec2(this.x / l, this.y / l) : new Vec2(0, 0);
  }

  withLength(l: number): Vec2 {
    return this.normalize().scale(l);
  }

  distanceTo(v: Vec2): number {
    return Math.hypot(this.x - v.x, this.y - v.y);
  }

  distanceSqTo(v: Vec2): number {
    const dx = this.x - v.x;
    const dy = this.y - v.y;
    return dx * dx + dy * dy;
  }

  angle(): number {
    return Math.atan2(this.y, this.x);
  }

  /** Signed angle from this vector to `v`, in (-PI, PI]. */
  angleTo(v: Vec2): number {
    return Math.atan2(this.cross(v), this.dot(v));
  }

  /** Left-hand normal (rotated 90 degrees counter-clockwise). */
  perp(): Vec2 {
    return new Vec2(-this.y, this.x);
  }

  rotate(rad: number, origin: Vec2 = ZERO): Vec2 {
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const dx = this.x - origin.x;
    const dy = this.y - origin.y;
    return new Vec2(origin.x + dx * c - dy * s, origin.y + dx * s + dy * c);
  }

  lerp(to: Vec2, t: number): Vec2 {
    return new Vec2(this.x + (to.x - this.x) * t, this.y + (to.y - this.y) * t);
  }

  equals(v: Vec2, eps = 1e-6): boolean {
    return Math.abs(this.x - v.x) <= eps && Math.abs(this.y - v.y) <= eps;
  }

  isZero(eps = 1e-6): boolean {
    return Math.abs(this.x) <= eps && Math.abs(this.y) <= eps;
  }

  toArray(): [number, number] {
    return [this.x, this.y];
  }
}

export const ZERO = Vec2.of(0, 0);

export class BBox {
  constructor(
    readonly minX: number,
    readonly minY: number,
    readonly maxX: number,
    readonly maxY: number,
  ) {}

  static empty(): BBox {
    return new BBox(Infinity, Infinity, -Infinity, -Infinity);
  }

  static fromPoints(points: readonly Vec2[], padding = 0): BBox {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
    return new BBox(minX - padding, minY - padding, maxX + padding, maxY + padding);
  }

  static fromCorners(a: Vec2, b: Vec2): BBox {
    return new BBox(
      Math.min(a.x, b.x),
      Math.min(a.y, b.y),
      Math.max(a.x, b.x),
      Math.max(a.y, b.y),
    );
  }

  static around(p: Vec2, radius: number): BBox {
    return new BBox(p.x - radius, p.y - radius, p.x + radius, p.y + radius);
  }

  get isEmpty(): boolean {
    return !(this.maxX >= this.minX && this.maxY >= this.minY);
  }

  get width(): number {
    return this.isEmpty ? 0 : this.maxX - this.minX;
  }

  get height(): number {
    return this.isEmpty ? 0 : this.maxY - this.minY;
  }

  get center(): Vec2 {
    return new Vec2((this.minX + this.maxX) / 2, (this.minY + this.maxY) / 2);
  }

  get min(): Vec2 {
    return new Vec2(this.minX, this.minY);
  }

  get max(): Vec2 {
    return new Vec2(this.maxX, this.maxY);
  }

  union(other: BBox): BBox {
    if (this.isEmpty) return other;
    if (other.isEmpty) return this;
    return new BBox(
      Math.min(this.minX, other.minX),
      Math.min(this.minY, other.minY),
      Math.max(this.maxX, other.maxX),
      Math.max(this.maxY, other.maxY),
    );
  }

  include(p: Vec2): BBox {
    if (this.isEmpty) return new BBox(p.x, p.y, p.x, p.y);
    return new BBox(
      Math.min(this.minX, p.x),
      Math.min(this.minY, p.y),
      Math.max(this.maxX, p.x),
      Math.max(this.maxY, p.y),
    );
  }

  expand(padding: number): BBox {
    if (this.isEmpty) return this;
    return new BBox(
      this.minX - padding,
      this.minY - padding,
      this.maxX + padding,
      this.maxY + padding,
    );
  }

  intersects(other: BBox, eps = 0): boolean {
    if (this.isEmpty || other.isEmpty) return false;
    return (
      this.minX <= other.maxX + eps &&
      this.maxX >= other.minX - eps &&
      this.minY <= other.maxY + eps &&
      this.maxY >= other.minY - eps
    );
  }

  containsPoint(p: Vec2, eps = 0): boolean {
    if (this.isEmpty) return false;
    return (
      p.x >= this.minX - eps &&
      p.x <= this.maxX + eps &&
      p.y >= this.minY - eps &&
      p.y <= this.maxY + eps
    );
  }

  containsBox(other: BBox, eps = 0): boolean {
    if (this.isEmpty || other.isEmpty) return false;
    return (
      other.minX >= this.minX - eps &&
      other.maxX <= this.maxX + eps &&
      other.minY >= this.minY - eps &&
      other.maxY <= this.maxY + eps
    );
  }

  /** Counter-clockwise from the lower-left corner. */
  corners(): Vec2[] {
    return [
      new Vec2(this.minX, this.minY),
      new Vec2(this.maxX, this.minY),
      new Vec2(this.maxX, this.maxY),
      new Vec2(this.minX, this.maxY),
    ];
  }

  toString(): string {
    return `[${this.minX}, ${this.minY} .. ${this.maxX}, ${this.maxY}]`;
  }
}

export function deg2rad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function rad2deg(rad: number): number {
  return (rad * 180) / Math.PI;
}

export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** Maps any angle into [0, 2PI). */
export function normalizeAngle(angle: number): number {
  const r = angle % TAU;
  return r < 0 ? r + TAU : r;
}

/** Counter-clockwise sweep from `start` to `end`; a full turn when they coincide. */
export function arcSweep(start: number, end: number, ccw: boolean): number {
  const raw = ccw ? normalizeAngle(end - start) : normalizeAngle(start - end);
  return raw < 1e-12 ? TAU : raw;
}

export function angleInArc(
  angle: number,
  start: number,
  end: number,
  ccw: boolean,
): boolean {
  const a = normalizeAngle(angle);
  const s = normalizeAngle(start);
  const e = normalizeAngle(end);
  if (Math.abs(normalizeAngle(e - s)) < 1e-12) return true;
  const tol = 1e-9;
  if (ccw) return normalizeAngle(a - s) <= normalizeAngle(e - s) + tol;
  return normalizeAngle(s - a) <= normalizeAngle(s - e) + tol;
}

/** 2D affine transform: x' = a*x + c*y + e, y' = b*x + d*y + f. */
export class Affine {
  constructor(
    readonly a = 1,
    readonly b = 0,
    readonly c = 0,
    readonly d = 1,
    readonly e = 0,
    readonly f = 0,
  ) {}

  static identity(): Affine {
    return new Affine();
  }

  static translation(dx: number, dy: number): Affine {
    return new Affine(1, 0, 0, 1, dx, dy);
  }

  static rotation(rad: number, origin: Vec2 = ZERO): Affine {
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const { x, y } = origin;
    return new Affine(c, s, -s, c, x - c * x + s * y, y - s * x - c * y);
  }

  static scaling(sx: number, sy: number = sx, origin: Vec2 = ZERO): Affine {
    const { x, y } = origin;
    return new Affine(sx, 0, 0, sy, x - sx * x, y - sy * y);
  }

  /** this ∘ other (apply `other` first). */
  multiply(other: Affine): Affine {
    return new Affine(
      this.a * other.a + this.c * other.b,
      this.b * other.a + this.d * other.b,
      this.a * other.c + this.c * other.d,
      this.b * other.c + this.d * other.d,
      this.a * other.e + this.c * other.f + this.e,
      this.b * other.e + this.d * other.f + this.f,
    );
  }

  apply(p: Vec2): Vec2 {
    return new Vec2(
      this.a * p.x + this.c * p.y + this.e,
      this.b * p.x + this.d * p.y + this.f,
    );
  }

  applyVector(v: Vec2): Vec2 {
    return new Vec2(this.a * v.x + this.c * v.y, this.b * v.x + this.d * v.y);
  }

  get determinant(): number {
    return this.a * this.d - this.b * this.c;
  }

  /** Uniform scale factor of the transform. */
  get scaleFactor(): number {
    return Math.sqrt(Math.abs(this.determinant));
  }

  get rotationAngle(): number {
    return Math.atan2(this.b, this.a);
  }
}
